import { resourcesCopy } from '@/copy/resources'
import { Button } from '@/ui/Button/Button'
import { Field, Input, Select } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { IconButton } from '@/ui/IconButton/IconButton'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { FieldGroup } from './FieldGroup'
import styles from './PredicateTermRows.module.css'
import { COMPARISONS, isList, type TermRow } from './predicate'

interface Props {
  label: string
  help: string
  rows: TermRow[]
  onRows: (rows: TermRow[]) => void
  /** `surface` inside a default Card (a normalize rule), D190. */
  ground?: 'page' | 'surface'
}

const BLANK: TermRow = { when: '', op: 'contains', value: '' }

/** Editable list of predicate terms: `when` template, comparison, value. */
export function PredicateTermRows({
  label,
  help,
  rows,
  onRows,
  ground = 'page',
}: Props) {
  const copy = resourcesCopy.forms.predicate
  const update = (i: number, patch: Partial<TermRow>) =>
    onRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  return (
    <FieldGroup label={label} help={help}>
      {rows.length > 0 && (
        <Stack gap="space-8">
          {rows.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity; order is the identity
            <Inline key={i} gap="space-8" align="end">
              <Field label={copy.when} className={styles.path}>
                <Input
                  mono
                  value={row.when}
                  placeholder={copy.rows.pathPlaceholder}
                  onChange={(e) => update(i, { when: e.target.value })}
                />
              </Field>
              <Field label={copy.comparison}>
                <Select
                  value={row.op}
                  onChange={(e) =>
                    update(i, { op: e.target.value as TermRow['op'] })
                  }
                >
                  {COMPARISONS.map((k) => (
                    <option key={k} value={k}>
                      {copy.comparisons[k]}
                    </option>
                  ))}
                  <option value="exists">{copy.comparisons.exists}</option>
                </Select>
              </Field>
              {row.op !== 'exists' && (
                <Field
                  label={isList(row.op) ? copy.values : copy.value}
                  className={styles.value}
                >
                  <Input
                    value={row.value}
                    placeholder={
                      isList(row.op)
                        ? copy.rows.listPlaceholder
                        : copy.rows.valuePlaceholder
                    }
                    onChange={(e) => update(i, { value: e.target.value })}
                  />
                </Field>
              )}
              <IconButton
                label={copy.rows.remove(label, i + 1)}
                ground={ground}
                onClick={() => onRows(rows.filter((_, j) => j !== i))}
              >
                <Icon name="cross-2" />
              </IconButton>
            </Inline>
          ))}
        </Stack>
      )}
      <Inline gap="space-8">
        <Button
          variant="ghost"
          ground={ground}
          onClick={() => onRows([...rows, BLANK])}
        >
          <Icon name="plus" size="icon-embedded" />
          {copy.rows.add}
        </Button>
      </Inline>
    </FieldGroup>
  )
}
